import { UseUserAuth } from './context/UseAuthContext';
import PageNav from "./components/PageNav";
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { generateSlug } from './utils';
import app from "./context/Firebase";
import { getAnalytics, logEvent } from "firebase/analytics";

export default function PrintSuccess() {
    return (
        <>
            <PageNav />
            <Heading />
        </>
    )
}

function Heading() {
    const { user } = UseUserAuth();
    const navigate = useNavigate();
    const [userName, setUserName] = useState("");
    const [userPaymentInfo, setUserPaymentInfo] = useState({});
    const [isRedirecting, setIsRedirecting] = useState(false);

    const fetchUserName = async () => {
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getUserName`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch user name");
            }

            const data = await response.json();
            setUserName(data);
        } catch (error) {
            console.error("Error fetching user name:", error);
        }
    };

    const fetchLatestPaymentInfo = async () => {
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getLatestPrintBookPayment`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch user payment");
            }

            const data = await response.json();
            const orderId = data["orderID"];
            const storyId = data["storyID"];
            const storyTitle = data["storyTitle"];

            const result = {
                orderId: orderId,
                storyId: storyId,
                storyTitle: storyTitle
            };
            setUserPaymentInfo(result);
        } catch (error) {
            console.error("Error fetching user payment:", error);
        }
    };

    // Redirect to print-book-order page
    const redirectToOrderProcessing = () => {
        if (userPaymentInfo.storyTitle && userPaymentInfo.storyId && userPaymentInfo.orderId) {
            setIsRedirecting(true);
            const slug = generateSlug(userPaymentInfo.storyTitle);
            
            // Navigate to print-book-order/slug page
            navigate(`/print-book-order/${slug}`, {
                state: {
                    storyId: userPaymentInfo.storyId,
                    storyTitle: userPaymentInfo.storyTitle,
                    orderId: userPaymentInfo.orderId
                }
            });
        }
    };

    useEffect(() => {
        if (user) {
            console.log("User detected, fetching user data...");
            fetchUserName();
            fetchLatestPaymentInfo();
        }
    }, [user]);

    // Auto-redirect when payment info is loaded
    useEffect(() => {
        if (Object.keys(userPaymentInfo).length > 0 && !isRedirecting) {
            // Track analytics and meta events when payment info is available
            const saleEndDate = new Date('2026-12-31T23:59:59');
            const currentDate = new Date();
            const isSaleActive = currentDate <= saleEndDate;
            const originalPrice = 25.00;
            const price = isSaleActive ? 20.00 : 25.00;
            
           // const price = 20; // Fixed price for print book
            const quantity = 1;
            const item_name = "Printed Storybook";
            const value = price;
            const transaction_id = userPaymentInfo.orderId;
            
            let item_id = 2; // Printed Book ID
            const currency = "USD";
            const items = [{ item_id, item_name, price, quantity }];
            const userInfo = { currency, value, transaction_id, items };
            
            console.log("Processing payment analytics - userInfo:", userInfo);
            
            // Firebase Analytics (commented out as per original)
            // const analytics = getAnalytics(app);
            // logEvent(analytics, "purchase", userInfo);
            
            // Add Meta Pixel purchase event
            if (window.fbq) {
                window.fbq('track', 'Purchase', {
                    currency: currency,
                    value: value,
                    content_name: item_name
                });
                console.log("Meta Pixel purchase event tracked");
            }
            
            // Add a small delay to show the success message briefly
            const timer = setTimeout(() => {
                redirectToOrderProcessing();
            }, 5000); // 10 second delay

            return () => clearTimeout(timer);
        }
    }, [userPaymentInfo, isRedirecting]);

    return (
        <div>
            <div className="flex justify-center text-lg font-semibold p-4 box mt-3 text-center text-indigo-600">
                Welcome <br />
                {userName || (user ? (user.displayName ? user.displayName : user.email) : "Guest")}
            </div>
            
            <div className="flex justify-center bg-white px-6 py-10 lg:px-8">
                <div role="list" className="mt-8 max-w-2xl space-y-8 text-gray-600 text-justify">
                    <div className="flex gap-x-3">
                        <CheckBadgeIcon className="mt-1 h-5 w-10 flex-none text-indigo-600" aria-hidden="true" />
                        <span>
                            <strong className="font-semibold text-gray-900">Payment Received Successfully: </strong>
                            Thank you for your order! We have received your payment and are now processing your print book order.
                            {userPaymentInfo.storyTitle && (
                                <span>
                                    <br /><br />
                                    <strong>Story:</strong> {userPaymentInfo.storyTitle}<br />
                                    <strong>Order ID:</strong> {userPaymentInfo.orderId}
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            {/* Loading/Redirect Message */}
            <div className="flex justify-center p-4">
                <div className="text-center">
                    {Object.keys(userPaymentInfo).length === 0 ? (
                        <p className="text-gray-600">Loading order details...</p>
                    ) : isRedirecting ? (
                        <div>
                            <p className="text-indigo-600 font-medium">Redirecting to order processing...</p>
                            <div className="mt-2">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-gray-600 mb-4">Preparing your order processing page...</p>
                            <button
                                onClick={redirectToOrderProcessing}
                                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                                Continue to Order Processing
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}